import axios from 'axios';
import type { BibleVerse, BibleChapter, APIResponse } from './types';
import { bibleCache } from './cache';
import { bibleVersions } from './bibleVersions';
import { bibleService } from '../services/bibleService';

const BIBLE_API_URL = 'https://bible-api.com';
const SCRIPTURE_API_URL = 'https://api.scripture.api.bible/v1';
const BIBLE_API_KEY = import.meta.env.VITE_BIBLE_API_KEY;

// List of single-chapter books
const SINGLE_CHAPTER_BOOKS = [
  'obadiah',
  'philemon',
  '2-john',
  '3-john',
  'jude'
];

// Check if a book is a single-chapter book
function isSingleChapterBook(book: string): boolean {
  const normalizedBook = book.toLowerCase().replace(/\s+/g, '-');
  return SINGLE_CHAPTER_BOOKS.includes(normalizedBook);
}

// Format book name for API requests
function formatBookName(book: string): string {
  return book.toLowerCase().replace(/[-\s]+/g, '+');
}

// Ensure response data is serializable
function sanitizeVerseData(verse: any): BibleVerse {
  return {
    id: String((verse.book_name || '') + '-' + (verse.chapter || '') + '-' + (verse.verse || '')),
    reference: `${verse.book_name || ''} ${verse.chapter || ''}:${verse.verse || ''}`,
    text: String(verse.text || '').trim(),
    version: String(verse.translation_id || 'kjv'),
    book: String(verse.book_name || ''),
    chapter: Number(verse.chapter || 0),
    verse: Number(verse.verse || 0)
  };
}

export function getBibleVersions() {
  return Promise.resolve(bibleVersions);
}

export async function getChapter(version: string, book: string, chapter: number): Promise<BibleChapter> {
  console.log('getChapter called with:', { version, book, chapter });
  const cacheKey = `${version}:${book}:${chapter}`;
  const cached = bibleCache.get<BibleChapter>(version, book, chapter);
  if (cached) {
    console.log('Returning cached chapter');
    return cached;
  }

  try {
    const formattedBook = formatBookName(book);
    console.log('Formatted book name:', formattedBook);
    
    // Get chapter from Bible service
    const chapterData = await bibleService.getChapter(version, formattedBook, chapter);
    console.log('Got chapter data:', {
      totalVerses: chapterData.verses.length,
      firstVerse: chapterData.verses[0],
      lastVerse: chapterData.verses[chapterData.verses.length - 1]
    });

    // Cache the chapter data
    bibleCache.set(version, book, chapter, chapterData);
    return chapterData;
  } catch (error) {
    console.error('Error in getChapter:', error);
    throw error;
  }
}

export async function searchBible(version: string, query: string, limit: number = 100): Promise<BibleVerse[]> {
  if (!query.trim()) {
    return [];
  }

  if (!BIBLE_API_KEY) {
    console.error('Bible API key missing');
    throw new Error('Bible API key is not configured. Please add your API key to the .env file.');
  }

  try {
    let bibleId = version;

    // If the version is a short code (e.g., 'kjv'), look up the full Bible ID
    if (version.length < 10) {
      const bible = bibleVersions.find(b => b.id?.toLowerCase() === version.toLowerCase());
      if (!bible) {
        // If version not found, default to KJV
        bibleId = '7142879509583d59-04'; // KJV Bible ID
      } else {
        // Map short version IDs to API.Bible IDs
        const versionMap: { [key: string]: string } = {
          'kjv': '7142879509583d59-04',
          'web': '9879dbb7cfe39e4d-01',
          'asv': '06125adad2d5898a-01'
        };
        bibleId = versionMap[bible.id] || '7142879509583d59-04'; // Default to KJV if mapping not found
      }
    }

    // Perform the search with the specified limit
    const response = await axios.get(`${SCRIPTURE_API_URL}/bibles/${bibleId}/search`, {
      params: {
        query: query.trim(),
        limit: limit,
        sort: 'relevance',
      },
      headers: {
        'api-key': BIBLE_API_KEY
      }
    });

    if (!response.data?.data?.verses) {
      return [];
    }

    // Transform the API response to match our BibleVerse type
    return response.data.data.verses.map((verse: any) => {
      const reference = verse.reference;
      const [bookAndChapter, verseNum] = reference.split(':');
      const lastSpaceIndex = bookAndChapter.lastIndexOf(' ');
      const book = bookAndChapter.substring(0, lastSpaceIndex);
      const chapter = parseInt(bookAndChapter.substring(lastSpaceIndex + 1));
      
      return {
        id: verse.id,
        reference: verse.reference,
        text: verse.text.trim(),
        version: version,
        book: book,
        chapter: chapter,
        verse: parseInt(verseNum)
      };
    });

  } catch (error) {
    console.error('Error during Bible search:', error);
    throw error;
  }
}